import { db } from '../config/firebase';
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import { CoffeeBean } from '../types/coffee';
import { CustomizationService } from './CustomizationService';
import { coffeeService } from './coffeeService';

const COFFEE_COLLECTION = 'coffees';
const customizationService = new CustomizationService();

// Chế độ debug - set true để xem logs chi tiết
const DEBUG = false;

/**
 * Service để cập nhật giá cà phê từ dữ liệu customization
 */
export const priceUpdateService = {
  /**
   * Cập nhật giá cà phê từ dữ liệu customization
   * @returns Thông tin về số lượng cà phê đã cập nhật và lỗi (nếu có)
   */
  async updateCoffeePricesFromCustomization(): Promise<{ updated: number, noChanges: number, errors: string[] }> {
    const result = { updated: 0, noChanges: 0, errors: [] as string[] };
    
    try {
      // Lấy tất cả dữ liệu customization
      const customizations = await customizationService.getAllCustomizations();
      
      if (DEBUG) console.log(`Loaded ${customizations.length} customizations`);
      
      // Lấy tất cả dữ liệu cà phê
      const coffees = await coffeeService.getAllCoffees();
      
      if (DEBUG) console.log(`Loaded ${coffees.length} coffees`);
      
      // Ánh xạ coffees theo mã để truy cập nhanh
      const coffeeMap = new Map<string, CoffeeBean>();
      coffees.forEach(coffee => {
        if (coffee.codeCoffee) {
          coffeeMap.set(coffee.codeCoffee, coffee);
        }
      });
      
      if (DEBUG) console.log(`Mapped ${coffeeMap.size} coffees by code`);
      
      // Duyệt qua từng customization để tìm các dishCode liên quan đến cà phê
      for (const customization of customizations) {
        if (DEBUG) console.log(`Processing customization: ${customization.name}`);
        
        // Kiểm tra các dishCode trong customization có phải là mã cà phê không
        for (const dishCode of customization.dishCode) {
          if (DEBUG) console.log(`Checking dishCode: ${dishCode}`);
          
          // Kiểm tra nếu dishCode có tiền tố "HAT-" hoặc mã cà phê đang tồn tại trong hệ thống
          if (dishCode.startsWith('HAT-') || coffeeMap.has(dishCode)) {
            if (DEBUG) console.log(`Found matching coffee code: ${dishCode}`);
            
            const coffee = coffeeMap.get(dishCode);
            
            if (coffee && coffee.id) {
              if (DEBUG) console.log(`Processing coffee: ${coffee.name} (ID: ${coffee.id})`);
              
              try {
                // Tạo mảng weightAndPrice mới từ dữ liệu customization
                const newWeightAndPrice: { weight: number; price: number }[] = [];
                let hasChanges = false;
                
                if (DEBUG) console.log(`Current weight and price: ${JSON.stringify(coffee.weightAndPrice)}`);
                
                // Duyệt qua các groups trong customization
                for (const group of customization.groups) {
                  if (DEBUG) console.log(`Processing group: ${group.groupName}`);
                  
                  for (const dish of group.dishCodes) {
                    if (DEBUG) console.log(`Processing dish: ${dish.name}, price: ${dish.price}`);
                    
                    // Trích xuất weight từ tên sản phẩm (dùng các pattern phổ biến)
                    // Pattern 1: "100G", "200G", "100g", etc.
                    let weightMatch = dish.name.match(/(\d+)G/i);
                    
                    // Pattern 2: "100 gram", "200 g", etc.
                    if (!weightMatch) {
                      weightMatch = dish.name.match(/(\d+)\s*(?:gram|g)/i);
                    }
                    
                    // Pattern 3: Tên sản phẩm là một số như "100", "200"
                    if (!weightMatch && /^\d+$/.test(dish.name)) {
                      weightMatch = [dish.name, dish.name];
                    }
                    
                    // Pattern 4: Sử dụng code như "100-COSTARICA", "200-COSTARICA"
                    if (!weightMatch && dish.code) {
                      const codeWeightMatch = dish.code.match(/^(\d+)-/);
                      if (codeWeightMatch) {
                        weightMatch = codeWeightMatch;
                      }
                    }
                    
                    if (weightMatch && weightMatch[1]) {
                      const weight = parseInt(weightMatch[1], 10);
                      const price = dish.price;
                      
                      if (DEBUG) console.log(`Extracted weight: ${weight}, price: ${price}`);
                      
                      if (weight > 0 && price > 0) {
                        // Kiểm tra trùng lặp trước khi thêm
                        if (!newWeightAndPrice.some(item => item.weight === weight)) {
                          newWeightAndPrice.push({ weight, price });
                          
                          // Kiểm tra xem có sự thay đổi so với giá hiện tại không
                          const existingPriceItem = coffee.weightAndPrice?.find(item => item.weight === weight);
                          if (!existingPriceItem || existingPriceItem.price !== price) {
                            if (DEBUG) console.log(`Price changed for weight ${weight}: ${existingPriceItem?.price} -> ${price}`);
                            hasChanges = true;
                          }
                        } else if (DEBUG) {
                          console.log(`Duplicate weight ${weight} found, using first occurrence`);
                        }
                      }
                    } else if (DEBUG) {
                      console.log(`Could not extract weight from dish name: ${dish.name}, code: ${dish.code}`);
                    }
                  }
                }
                
                // Nếu không tìm thấy weight và price trong các group,
                // tìm kiếm trong customization code
                if (newWeightAndPrice.length === 0) {
                  if (DEBUG) console.log('No weight and price found in groups, checking dish codes');
                  
                  for (const code of customization.dishCode) {
                    // Tìm kiếm pattern như "100-COSTARICA", "200-COSTARICA"
                    if (code.includes('-')) {
                      const parts = code.split('-');
                      if (parts.length >= 2) {
                        const weightStr = parts[0];
                        if (/^\d+$/.test(weightStr)) {
                          const weight = parseInt(weightStr, 10);
                          
                          // Tìm giá trong groups
                          for (const group of customization.groups) {
                            const matchingDish = group.dishCodes.find(dish => dish.code === code);
                            if (matchingDish && matchingDish.price > 0) {
                              if (DEBUG) console.log(`Found match in dishCode: ${code}, weight: ${weight}, price: ${matchingDish.price}`);
                              
                              newWeightAndPrice.push({ weight, price: matchingDish.price });
                              
                              // Kiểm tra thay đổi
                              const existingPriceItem = coffee.weightAndPrice?.find(item => item.weight === weight);
                              if (!existingPriceItem || existingPriceItem.price !== matchingDish.price) {
                                hasChanges = true;
                              }
                              break;
                            }
                          }
                        }
                      }
                    }
                  }
                }
                
                if (DEBUG) console.log(`New weight and price data: ${JSON.stringify(newWeightAndPrice)}`);
                
                // Chỉ cập nhật nếu có thay đổi và có ít nhất một sản phẩm hợp lệ
                if (hasChanges && newWeightAndPrice.length > 0) {
                  // Sắp xếp theo trọng lượng tăng dần
                  newWeightAndPrice.sort((a, b) => a.weight - b.weight);
                  
                  if (DEBUG) console.log(`Updating coffee ${coffee.id} with new prices`);
                  
                  await coffeeService.updateCoffee(coffee.id, {
                    weightAndPrice: newWeightAndPrice
                  });
                  result.updated++;
                  
                  if (DEBUG) console.log(`Updated coffee ${coffee.name} with new prices`);
                } else if (newWeightAndPrice.length > 0) {
                  // Có dữ liệu nhưng không có thay đổi
                  result.noChanges++;
                  if (DEBUG) console.log(`No changes for coffee ${coffee.name}`);
                } else {
                  if (DEBUG) console.log(`No valid weight and price data found for coffee ${coffee.name}`);
                }
              } catch (error: any) {
                console.error(`Error updating coffee ${dishCode}:`, error);
                result.errors.push(`Không thể cập nhật cà phê ${dishCode}: ${error.message || String(error)}`);
              }
            } else if (DEBUG) {
              console.log(`Coffee with code ${dishCode} not found or has no ID`);
            }
          }
        }
      }
      
      if (DEBUG) console.log(`Price update completed. Updated: ${result.updated}, No changes: ${result.noChanges}, Errors: ${result.errors.length}`);
    } catch (error: any) {
      console.error('Error updating coffee prices:', error);
      result.errors.push(`Lỗi trong quá trình cập nhật giá: ${error.message || String(error)}`);
    }
    
    return result;
  }
}; 